import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Checkbox } from "north.js";
import React from "react";
import styled from "styled-components";

import { INTEREST_OPTIONS } from "src/views/profile/components/ProfileForm/constants";

export interface StagedFilters {
  textQuery: string;
  interests: string[];
  weekendPace: number[];
  teammatesNeeded: number[];
}

export type FilterSection = "lookingFor" | "weekendPace" | "projectInterests";

export type ExpandedFilterSections = Record<FilterSection, boolean>;

const interestOptions = INTEREST_OPTIONS.map((interest) => ({
  value: interest,
  label: interest,
}));

const teammateOptions = [
  { value: 3, label: "3 Teammates" },
  { value: 2, label: "2 Teammates" },
  { value: 1, label: "1 Teammate" },
  { value: 0, label: "0 Teammates" },
];

const weekendPaceOptions = [
  { value: 0, label: "Up for anything" },
  { value: 1, label: "Mild" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Maximum" },
];

const FilterPanel = styled.aside`
  display: flex;
  box-sizing: border-box;
  width: 195px;
  min-width: 195px;
  min-height: 0;
  max-height: inherit;
  align-self: flex-start;
  flex-direction: column;
  padding: 4px 4px 24px;

  @media (max-width: 900px) {
    width: 100%;
    min-width: 0;
    padding-bottom: 0;
  }
`;

const FilterOptions = styled.div`
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: ${({ theme }) => theme.globalConstants.color.borderPrimary}
    transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline-offset: -2px;
  }
`;

const Section = styled.section`
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};

  & + & {
    padding-top: 16px;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const SectionToggle = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  cursor: pointer;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  text-align: left;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const SectionChevron = styled(ChevronDownIcon)<{ $expanded: boolean }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform 150ms ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  padding-left: 8px;

  label {
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
    font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
    font-size: 16px;
    line-height: 24px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  padding-top: 16px;
  background-color: ${({ theme }) => theme.globalConstants.color.white};
`;

const ClearButton = styled.button`
  padding: 10px 20px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 4px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 700;
  line-height: normal;

  &:hover {
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  }
`;

const toggleValue = <T extends string | number>(values: T[], value: T): T[] =>
  values.includes(value)
    ? values.filter((current) => current !== value)
    : [...values, value];

interface CheckboxSectionProps<T extends string | number> {
  id: string;
  title: string;
  expanded: boolean;
  options: Array<{ value: T; label: string }>;
  selectedValues: T[];
  onToggleExpanded: () => void;
  onToggle: (value: T) => void;
}

const CheckboxSection = <T extends string | number>({
  id,
  title,
  expanded,
  options,
  selectedValues,
  onToggleExpanded,
  onToggle,
}: CheckboxSectionProps<T>) => {
  return (
    <Section>
      <SectionToggle
        type="button"
        aria-controls={id}
        aria-expanded={expanded}
        onClick={onToggleExpanded}
      >
        {title}
        <SectionChevron $expanded={expanded} aria-hidden="true" />
      </SectionToggle>
      {expanded && (
        <CheckboxList id={id}>
          {options.map(({ value, label }) => (
            <Checkbox
              key={value}
              checked={selectedValues.includes(value)}
              label={label}
              onChange={() => onToggle(value)}
            />
          ))}
        </CheckboxList>
      )}
    </Section>
  );
};

const DirectoryFilters: React.FC<{
  filters: StagedFilters;
  expandedSections: ExpandedFilterSections;
  onChange: (filters: StagedFilters) => void;
  onToggleSection: (section: FilterSection) => void;
}> = ({ filters, expandedSections, onChange, onToggleSection }) => (
  <FilterPanel id="directory-filter-panel" aria-label="Directory filters">
    <FilterOptions tabIndex={0} aria-label="Scrollable filter options">
      <CheckboxSection
        id="directory-looking-for-filters"
        title="Looking for"
        expanded={expandedSections.lookingFor}
        options={teammateOptions}
        selectedValues={filters.teammatesNeeded}
        onToggleExpanded={() => onToggleSection("lookingFor")}
        onToggle={(count) =>
          onChange({
            ...filters,
            teammatesNeeded: toggleValue(filters.teammatesNeeded, count),
          })
        }
      />

      <CheckboxSection
        id="directory-weekend-pace-filters"
        title="Weekend Pace"
        expanded={expandedSections.weekendPace}
        options={weekendPaceOptions}
        selectedValues={filters.weekendPace}
        onToggleExpanded={() => onToggleSection("weekendPace")}
        onToggle={(pace) =>
          onChange({
            ...filters,
            weekendPace: toggleValue(filters.weekendPace, pace),
          })
        }
      />

      <CheckboxSection
        id="directory-project-interest-filters"
        title="Project Interests"
        expanded={expandedSections.projectInterests}
        options={interestOptions}
        selectedValues={filters.interests}
        onToggleExpanded={() => onToggleSection("projectInterests")}
        onToggle={(interest) =>
          onChange({
            ...filters,
            interests: toggleValue(filters.interests, interest),
          })
        }
      />
    </FilterOptions>

    <Actions>
      <ClearButton
        type="button"
        onClick={() =>
          onChange({
            ...filters,
            interests: [],
            weekendPace: [],
            teammatesNeeded: [],
          })
        }
      >
        Clear All Filters
      </ClearButton>
    </Actions>
  </FilterPanel>
);

export default DirectoryFilters;
